import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { AvailabilityRow, BlockedSlot } from '@/types';

interface UseAvailabilityReturn {
  barberId: string | null;
  availability: AvailabilityRow[];
  blockedSlots: BlockedSlot[];
  loading: boolean;
  error: string | null;
  saveAvailabilityRow: (row: Omit<AvailabilityRow, 'id' | 'barber_id' | 'created_at' | 'updated_at'>) => Promise<void>;
  addBlockedSlot: (slot: Omit<BlockedSlot, 'id' | 'barber_id' | 'created_at'>) => Promise<void>;
  removeBlockedSlot: (id: string) => Promise<void>;
  refetch: () => void;
}

export function useAvailability(): UseAvailabilityReturn {
  const [barberId, setBarberId] = useState<string | null>(null);
  const [availability, setAvailability] = useState<AvailabilityRow[]>([]);
  const [blockedSlots, setBlockedSlots] = useState<BlockedSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        // Get active barber
        const { data: barberData, error: barberErr } = await supabase
          .from('barbers')
          .select('id')
          .eq('active', true)
          .limit(1)
          .maybeSingle();

        if (barberErr || !barberData) {
          throw new Error('No active barber found');
        }

        const bid = barberData.id as string;
        if (!cancelled) setBarberId(bid);

        // Fetch availability
        const { data: avData, error: avErr } = await supabase
          .from('availability')
          .select('*')
          .eq('barber_id', bid)
          .order('day_of_week', { ascending: true });

        if (avErr) throw new Error(avErr.message);

        // Fetch blocked slots (future + today)
        const today = new Date().toISOString().slice(0, 10);
        const { data: bsData, error: bsErr } = await supabase
          .from('blocked_slots')
          .select('*')
          .eq('barber_id', bid)
          .gte('blocked_date', today)
          .order('blocked_date', { ascending: true });

        if (bsErr) throw new Error(bsErr.message);

        if (!cancelled) {
          setAvailability((avData ?? []) as AvailabilityRow[]);
          setBlockedSlots((bsData ?? []) as BlockedSlot[]);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [tick]);

  const saveAvailabilityRow = useCallback(
    async (row: Omit<AvailabilityRow, 'id' | 'barber_id' | 'created_at' | 'updated_at'>) => {
      if (!barberId) return;
      const { error: upsertErr } = await supabase
        .from('availability')
        .upsert(
          { ...row, barber_id: barberId, updated_at: new Date().toISOString() },
          { onConflict: 'barber_id,day_of_week' }
        );
      if (upsertErr) throw new Error(upsertErr.message);
      refetch();
    },
    [barberId, refetch]
  );

  const addBlockedSlot = useCallback(
    async (slot: Omit<BlockedSlot, 'id' | 'barber_id' | 'created_at'>) => {
      if (!barberId) return;
      const { error: insertErr } = await supabase
        .from('blocked_slots')
        .insert({ ...slot, barber_id: barberId });
      if (insertErr) throw new Error(insertErr.message);
      refetch();
    },
    [barberId, refetch]
  );

  const removeBlockedSlot = useCallback(
    async (id: string) => {
      const { error: delErr } = await supabase
        .from('blocked_slots')
        .delete()
        .eq('id', id);
      if (delErr) throw new Error(delErr.message);
      refetch();
    },
    [refetch]
  );

  return {
    barberId,
    availability,
    blockedSlots,
    loading,
    error,
    saveAvailabilityRow,
    addBlockedSlot,
    removeBlockedSlot,
    refetch,
  };
}
